
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AppGlobals } from '@app/app.global';
import { SharedService } from '@app/core';
import { FieldType } from '@app/modules/invoice/interfaces';
import { InvoiceService } from '@modules/invoice/services';
import jspreadsheet from "jspreadsheet";
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-invoice-extraction',
  templateUrl: './invoice-extraction.component.html',
  styleUrls: ['./invoice-extraction.component.scss']
})
export class InvoiceExtractionComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('mergeConfirmation', { static: false }) mergeModal!: any;

  @Input() animateAI;
  @Input() expandAI;
  @Input() originalData;
  @Input() netTotal = 0;
  @Input() totalAmount = 0;
  @Input() invoiceId;
  @Input() page;
  @Input() autosaveInProgress;
  @Input() isSaveAndExitAIModeConfirmed;
  @Input() isMultiPage: boolean = false;
  @Output() toggleSelectionEmitter: EventEmitter<{
    isToggled: boolean,
    extractedData: any, extractedForPage: string | number
  }> = new EventEmitter();
  @Output() submitEmitter: EventEmitter<{}> = new EventEmitter();
  @Output() autosaveEmitter: EventEmitter<{}> = new EventEmitter();
  @Output() aiModeExitEmitter: EventEmitter<{}> = new EventEmitter();
  OriginalWorkSheet: jspreadsheet.worksheetInstance[] | any[];
  AIResultWorkSheet: jspreadsheet.worksheetInstance[] | any[];
  prompts = this.appGlobals.AI_EXTRACTION_PROMPTS;
  prompt = new FormControl(null);
  promptsForm: FormGroup;
  originalSpreadSheetData: any;
  isSheetLoaded = false;
  canShowAIResults = false;
  isRequestProcessing = false;
  autoSaveEmitted = false;
  isSaveAndExitModeEmitted = false;
  activeTab = 'tab-original';
  getAIPromptRequestDetailSubscription: Subscription;
  columnNames = [];
  extractedValues = [];
  extractedForPage: string | number = 'All';
  // PAGE-WISE STORAGE
  pages: any[] = [];
  currentPageIndex: number = 0;
  AIResultPage: number = 1;
  referenceColumns = [];
  modifiedColumns = [];

  constructor(private readonly appGlobals: AppGlobals,
    private readonly formBuilder: FormBuilder, private readonly invoiceService: InvoiceService,
    private readonly sharedService: SharedService) {
    this.initializePromptsFormGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // get original spreadsheet data
    if (changes.originalData?.currentValue) {
      this.originalSpreadSheetData = changes.originalData.currentValue;

      // Below code is to reinitialize original spreadsheet on page change
      if (this.OriginalWorkSheet?.length) {
        this.deleteOriginalSheet();
        this.configureOriginalSpreadsheet();
      }
    }

    // if autosave is emitted and autosave response is received then submit the form
    if (changes.autosaveInProgress?.currentValue && this.autoSaveEmitted) {
      this.onExtractData();
      this.autoSaveEmitted = false;
    }

    // check if save & exit AI Mode is confirmed
    if (changes.isSaveAndExitAIModeConfirmed?.currentValue && this.isSaveAndExitModeEmitted) {
      this.onSaveAndExit();
    }
  }

  ngOnInit(): void {

    if (!this.isMultiPage) {
      this.promptF.page?.setValue(false);
    }
  }

  ngAfterViewInit(): void {
    // Create the spreadsheet
    jspreadsheet.setLicense(this.appGlobals.JSpreadSheetLicense);
    this.configureOriginalSpreadsheet();

    const el: HTMLElement = this.mergeModal?.elementRef?.nativeElement;
    if (el && el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
  }

  initializePromptsFormGroup() {
    this.promptsForm = this.formBuilder.group({
      promptOption: [null],
      prompt: [null],
      page: [false]
    });
  }

  configureOriginalSpreadsheet() {
    this.isSheetLoaded = false;
    this.OriginalWorkSheet = jspreadsheet(document.getElementById('originalPTTable'), {
      worksheets: [{
        data: this.originalSpreadSheetData,
        worksheetName: 'originalPTWorksheet' + new Date().getTime(),
        columnDrag: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        tableOverflow: false,
        columnSorting: false,
        defaultColWidth: '120px',
        resize: 'both',
      }],
      autoCasting: false,
      contextMenu: function () {
        return [];
      },
      oncreatecell: (worksheet, cell, column, row, value) => {
        this.setType(worksheet, column, row, value);
      },
      onload: () => {
        this.isSheetLoaded = true;
        setTimeout(() => {
          this.freezeOriginalSheetHeading();
        }, 0);
      },
      onbeforechange: () => {
        if (this.isSheetLoaded) {
          this.sharedService.errorAlert('Modifications are not allowed in original data sheet.');
          return false;
        }
      }
    });
  }

  initializeAIResultSheet(columnData: any[]) {
    this.AIResultWorkSheet = jspreadsheet(document.getElementById('AIResultData'), {
      worksheets: [{
        data: columnData,
        worksheetName: 'AIResultData' + new Date().getTime(),
        minDimensions: [1, columnData.length],
        columnDrag: false,
        allowManualInsertColumn: false,
        allowDeleteColumn: false,
        tableOverflow: false,
        columnSorting: false,
        defaultColWidth: '120px',
        resize: 'both',
      }],
      autoCasting: false,
      contextMenu: function () {
        return [];
      },
      onload: () => { },
      onbeforechange: () => { }
    });
  }

  /**
   * This method will set dropdown and type in first column for Purchase and Expense
   * @param worksheet 
   * @param column 
   * @param row 
   * @param value 
   */
  setType = (worksheet, column: number, row: number, value) => {
    if (column !== undefined && column === 0 && row !== undefined && row !== 0) { //This will check if it is first column
      setTimeout(() => { // removed timeout as it takes longer to load dropdown in case of Multipage Invoices - when invoice page is changed
        try {
          worksheet.setProperties(column, row, { // If we remove timeout then it throws error in console while adding new row
            type: 'dropdown',
            source: [FieldType.PURCHASE, FieldType.EXPENSE],
            autocomplete: true,
            options: { autofocus: true }
          });
        } catch (error) { }
      }, 0);

      if (this.originalData[row] && '__type' in this.originalData[row]) { // if __type == true, set cell value = Expense otherwise set cell value = Purchase
        worksheet.updateCell(column, row, this.originalData[row].__type ? FieldType.EXPENSE : FieldType.PURCHASE);
      }
    }

  }

  async freezeOriginalSheetHeading() {
    this.OriginalWorkSheet[0]?.setFreezeRows(1);
  }

  get promptF() {
    return this.promptsForm.controls;
  }

  /**
   * This method wll highlight extracted columns in AI result sheet
   */
  highlightExtractedColumns() {
    if (this.modifiedColumns?.length && this.AIResultWorkSheet?.length) {
      const headers = this.AIResultWorkSheet[0].getHeaders().split(',');
      this.modifiedColumns.forEach((columnName) => {

        const findColumnIndex = headers?.findIndex((heading) => heading.toString().toLowerCase() == columnName?.toLowerCase());

        if (findColumnIndex > -1) {
          const columnName = this.AIResultWorkSheet[0].helpers.getColumnName(findColumnIndex);
          const columnData = this.AIResultWorkSheet[0].getColumnData(findColumnIndex);

          if (columnName && columnData.length) {
            columnData.forEach((column, columnIndex) => {
              this.AIResultWorkSheet[0].setStyle(`${columnName}+${columnIndex + 1}`, `background-color: #fffbf0; color:#000`);
            });
          }
        }

      })
    }
  }

  /**
   * This method will exit the AI mode 
   */
  exitAIMode() {
    this.toggleSelectionEmitter.emit({ isToggled: true, extractedData: [], extractedForPage: '' });
  }

  /**
   * This method will pass the extracted data to parent component and exit the AI mode
   */
  onSaveAndExit() {
    this.removeReferenceColumns();
    this.toggleSelectionEmitter.emit({ isToggled: true, extractedData: this.pages, extractedForPage: this.extractedForPage });
  }

  /**
   * This method will set the prompt value based on prompt option selected
   * @param promptOption 
   */
  onPromptChange(promptOption) {
    if (promptOption) {
      this.promptF.prompt.setValue(this.prompts.find((prompt) => prompt.id == promptOption)?.prompt);
    }
  }

  resetPromptsForm() {
    this.promptsForm.reset();
    this.promptF.page.setValue(false);
  }

  /**
   * This method will emit the autosave event to parent component
   */
  onEmitAutoSave() {
    if (this.promptF.prompt.value) {
      this.autoSaveEmitted = true;
      this.autosaveEmitter.emit();
    } else {
      this.sharedService.errorAlert('Please select from available prompts or enter custom prompt to proceed.');
    }
  }

  /**
   * This method will post AI extraction request
   */
  onExtractData() {
    const requestBody = this.promptsForm.getRawValue();
    delete requestBody.promptOption;

    if (requestBody.page) {
      requestBody['page'] = 'All';
    } else {
      requestBody['page'] = this.page + 1;
    }

    this.extractedForPage = requestBody['page'];

    requestBody['invoiceId'] = this.invoiceId;
    this.promptsForm.disable();
    this.invoiceService.postExtraction(requestBody).subscribe({
      next: (response) => {
        if (response) {
          this.onTabChange('tab-ai-result');
          this.sharedService.successAlert(response.message);
          if (response.requests?.length) {
            // this.setAIRequestsInStorage(response.requests);
            this.getAIRequestDetails(response.requests[0].batchId);
          }
        }
      },
      error: (error) => {
        console.log("error", error);
        this.sharedService.errorAlert(error);
        this.promptsForm.enable();
      }
    });
  }

  /**
   * This method will get AI prompt request details at regular interval
   * @param batchId 
   */
  getAIRequestDetails(batchId: string) {
    this.isRequestProcessing = true;
    this.canShowAIResults = false;

    this.getAIPromptRequestDetailSubscription = interval(10000).subscribe(() => {
      this.getAIData(batchId);
    });
  }

  /**
   * This method will get AI data for given batch Id
   * @param batchId 
   */
  getAIData(batchId: string) {
    this.invoiceService.getAIPromptRequestDetail(batchId).subscribe({
      next: (response) => {
        if (response) {
          if (response.data?.length) {

            const pendingRequest = response.data.find(
              (request) => request.runtimestatus != 'COMPLETED' && request.runtimestatus != 'FAILED'
            );

            if (!pendingRequest) {

              this.pages = []; // reset pages
              this.isRequestProcessing = false;

              // PROCESS EACH PAGE SEPARATELY
              for (const request of response.data) {

                for (const pageKey of Object.keys(request.response)) {

                  const productTable = request.response[pageKey].productTable;
                  let columnNames: string[] = [];
                  let rowMap: any = {};

                  for (const separatorKey of Object.keys(productTable)) { // reference_columns, modified_columns

                    for (const columnKey of Object.keys(productTable[separatorKey])) {
                      if (!columnNames.includes(columnKey)) {
                        columnNames.push(columnKey);

                        if (separatorKey == 'referenceColumns') {
                          if (!this.referenceColumns.includes(columnKey)) {
                            this.referenceColumns.push(columnKey);
                          }
                        } else if (separatorKey == 'modifiedColumns') {
                          if (!this.modifiedColumns.includes(columnKey)) {
                            this.modifiedColumns.push(columnKey);
                          }
                        }
                      }

                      if (this.referenceColumns?.length) {
                        const colData = productTable[separatorKey][columnKey];

                        for (const rowKey of Object.keys(colData)) {
                          if (!rowMap[rowKey]) rowMap[rowKey] = {};
                          rowMap[rowKey][columnKey] = colData[rowKey];
                        }
                      }
                    }
                  }

                  if (this.referenceColumns?.length) {
                    // Convert rowMap → 2D row array
                    const rows = Object.keys(rowMap).map(rowKey => {
                      return columnNames.map(col => rowMap[rowKey][col] || "");
                    });

                    // Push this page's dataset
                    this.pages.push({
                      pageName: pageKey,
                      columnNames,
                      rows
                    });
                  }

                }
              }

              // INITIALIZE FIRST PAGE
              if (this.pages.length > 0) {
                this.currentPageIndex = 0;
                this.loadPageIntoSpreadsheet(this.currentPageIndex);
                this.canShowAIResults = true;
              } else if (!this.modifiedColumns?.length || !this.referenceColumns?.length || !this.pages.length) {
                this.sharedService.errorAlert('Could not extract data successfully.');
                this.onTabChange('tab-original');
                this.canShowAIResults = false;
                this.isRequestProcessing = false;
              }

              this.getAIPromptRequestDetailSubscription.unsubscribe();
              this.resetPromptsForm();
              this.promptsForm.enable();
            }
          }

        }
      },
      error: (error) => {
        console.error('error', error);
        this.sharedService.errorAlert(error);
        this.deleteAIResultSheet();
      }
    });
  }

  loadPageIntoSpreadsheet(pageIndex: number) {
    const page = this.pages[pageIndex];
    if (!page) return;

    this.deleteAIResultSheet();
    this.initializeAIResultSheet(page.rows);

    page.columnNames.forEach((name, index) => {
      this.AIResultWorkSheet[0].setHeader(index, name);
    });
    this.highlightExtractedColumns();
  }

  nextPage() {
    if (this.currentPageIndex < this.pages.length - 1) {
      this.currentPageIndex++;
      this.loadPageIntoSpreadsheet(this.currentPageIndex);
    }
  }

  prevPage() {
    if (this.currentPageIndex > 0) {
      this.currentPageIndex--;
      this.loadPageIntoSpreadsheet(this.currentPageIndex);
    }
  }

  deleteAIResultSheet() {
    if (this.AIResultWorkSheet?.length) {
      this.AIResultWorkSheet[0].parent.destroy(document.getElementById('AIResultData'));
    }
  }

  deleteOriginalSheet() {
    if (this.OriginalWorkSheet?.length) {
      this.OriginalWorkSheet[0].parent.destroy(document.getElementById('originalPTTable'))
    }
  }

  setAIRequestsInStorage(aiRequests) {
    const AIRequestsFromStorage = this.sharedService.getAIExtractionRequests() || [];
    if (AIRequestsFromStorage?.length) {
      const findCurrentInvoice = AIRequestsFromStorage.findIndex((request) => request.invoiceId == this.invoiceId);
      if (findCurrentInvoice) {
        // push requests in current invoice object

      } else {
        // push new requests with invoice Id
        const requestsToPush = {
          invoiceId: this.invoiceId,
          requests: aiRequests
        }
        AIRequestsFromStorage.push(requestsToPush);
        this.sharedService.setAIExtractionsRequests(AIRequestsFromStorage);
      }
    } else {
      // First object as AI request will be pushed
      const requestsToPush = {
        invoiceId: this.invoiceId,
        requests: aiRequests
      }
      AIRequestsFromStorage.push(requestsToPush);
      this.sharedService.setAIExtractionsRequests(AIRequestsFromStorage);
    }
  }

  // setAIResultDataInSheet(columnData: any[]) {
  //   this.AIResultWorkSheet[0].setColumnData(0, columnData);
  //   this.canShowAIResults = true;
  //   console.log('this.AIResultWorkSheet[0]:', this.AIResultWorkSheet[0]);
  // }

  onTabChange(tab: string) {
    this.activeTab = tab;
  }

  onPageChange(event) {
    this.currentPageIndex = event - 1;
    this.loadPageIntoSpreadsheet(this.currentPageIndex);
  }

  onHideMergeConfirmation() { }

  onCancelMergeConfirmation() { }

  onCancelAIRequest(event) {
    event.preventDefault();
    this.isRequestProcessing = false;
    this.promptsForm.enable();
    this.getAIPromptRequestDetailSubscription?.unsubscribe();
    this.activeTab = 'tab-original';
  }

  onEmitSaveAndExitConfirmation() {
    this.isSaveAndExitModeEmitted = true;
    this.aiModeExitEmitter.emit();
  }

  /**
   * This method will remove reference columns from each page
   */
  removeReferenceColumns() {
    if (this.referenceColumns?.length && this.pages?.length) {
      for (const page of this.pages) {
        for (let index = page.columnNames.length - 1; index >= 0; index--) {
          const columnName = page.columnNames[index];

          if (this.referenceColumns.includes(columnName)) {
            // Remove the column
            page.columnNames.splice(index, 1);

            // Remove the entry in each row
            for (const row of page.rows) {
              row.splice(index, 1);
            }
          }
        }
      }

    }
  }

  ngOnDestroy(): void {
    this.getAIPromptRequestDetailSubscription?.unsubscribe();
  }


}
